import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { money } from './ui'
import { GridChrome, ResizeHandle, useGridLayout, type Pin } from './gridLayout'
import { SortHeader, compareBy, useGridSort, type SortState } from './gridSort'
import { PartlyWithheld, Withheld } from './Withheld'

/** `virtual`: a list longer than this draws only the rows in view, and this many either side. */
const VIRTUAL_FROM = 150
const OVERSCAN = 12

export interface Column<T> {
  key: string
  header: string
  align?: 'left' | 'right'
  /** Rendered value. Defaults to the raw field, formatted as money when numeric. */
  render?: (row: T) => ReactNode
  /** Pulls the summable number out of a row. Also sorts it, absent `sortValue`. */
  value?: (row: T) => number
  /**
   * What this column sorts by, when neither `value` nor the raw field says.
   *
   * NEEDED WHENEVER `render` IS. A rendered cell returns nodes — a person with
   * their email under their name, a badge, a button — and nodes cannot be
   * compared. Without this the column still offered a heading to click and
   * then did nothing at all, which is worse than not offering it: the reader
   * concludes the grid is broken rather than that the column is inert. Found
   * on Access > Platform roles, 20 Sep 2026, where every column renders.
   *
   * Return the thing the reader THINKS they are sorting by — the person's
   * name, not their object id.
   */
  sortValue?: (row: T) => string | number | null
  /**
   * The name the SERVER knows this column by, for a server-sorted grid.
   *
   * REQUIRED THERE, and deliberately not defaulted to `key`. A grid's keys are
   * its own short names — 'po', 'due', 'name' — while a procedure's sort names
   * are its output aliases — 'PoNumber', 'StillDue', 'ItemName'. Sending the
   * key would have been rejected by the procedure's closed list and silently
   * dropped to the default order, while the heading drew an arrow claiming it
   * had sorted. A wrong order that looks right is the worst outcome available
   * here, so a column with no `sortKey` simply does not offer the click.
   *
   * Ignored by a client-sorted grid, which compares values directly.
   */
  sortKey?: string
  /** Sum this column in the totals row. */
  total?: boolean
  /**
   * UNDER THE NEW PERMISSION MODEL (M8): whether this row's figure is withheld,
   * or leaves withheld values out ('partial'). The totals row then says so rather
   * than add a withheld figure as 0 (the build plan's R4-2): "withheld" when every
   * row withholds it, the sum with the partly-withheld mark when some do. Absent,
   * as on every grid under today's model, the totals row is a plain sum.
   */
  withheld?: (row: T) => 'withheld' | 'partial' | null
  /**
   * Cap this column's width, in `ch`, and truncate what overflows.
   *
   * FOR FREE TEXT ONLY, and the distinction matters. Headers are never
   * truncated and figures are never truncated — a clipped number is a wrong
   * number. But a free-text "Reason" carrying a sentence will size its column
   * to the longest sentence anybody has ever typed, and on the roles table that
   * pushed the Revoke button clean off the right-hand edge: the row's only
   * action, reachable exclusively by horizontal scrolling, which nobody does
   * because nothing suggests there is anything there.
   *
   * The full text stays in the title attribute, so nothing is lost — it is one
   * hover away rather than one scroll away.
   */
  clamp?: number
  /**
   * Pin this column to the right-hand edge, so it stays on screen however far
   * the table scrolls across.
   *
   * FOR THE ROW'S ACTION, and it is not the same fix as `clamp`. Clamping is a
   * guess about how wide the window is: narrow enough columns and the button
   * fits at 1440px, then somebody opens the panel at 1100px and it is gone
   * again. Pinning is the property actually wanted — "the way to act on this
   * row is always visible" — stated once instead of re-tuned per viewport.
   *
   * Only ever the LAST column. Two pinned columns would need cumulative offsets,
   * and nothing here needs two.
   */
  stickyRight?: boolean
  /**
   * Pin this column to the LEFT edge, so the reader can always tell which row
   * they are on however far the table has scrolled across.
   *
   * FOR THE ROW'S IDENTITY, and it is the counterpart of stickyRight. These
   * grids are wide on purpose — Purchase Orders measures 34 columns, and at
   * 1280x720 twenty-six of them are reachable only by scrolling right.
   * Without this, arriving at "Line Amount" means the item number that made it
   * meaningful is 2,914px behind you, and the only way to check which row you
   * are reading is to scroll all the way back. Every lookup became a round
   * trip.
   *
   * Only ever the FIRST column, for the same reason stickyRight is only ever
   * the last: two pinned columns on the same side need cumulative offsets and
   * nothing here needs two.
   */
  stickyLeft?: boolean
  /**
   * Start hidden, offered in the Columns panel: the grid's own default, not the
   * reader's choice (gridLayout's LayoutColumn). Only with a `gridId`, which is
   * what draws the panel; without one a hidden column could never be shown.
   */
  hiddenByDefault?: boolean
}

/**
 * The report grid.
 *
 * ---------------------------------------------------------------------------
 * HEADERS ARE NEVER TRUNCATED
 *
 * Columns are sized from their header text and the table scrolls horizontally
 * when the total exceeds the viewport. A truncated header ("Total Rec…") forces
 * the reader to guess which figure they are looking at, and in a financial
 * report guessing is the failure. So the trade-off runs the opposite way from
 * most grids: the header always wins and the viewport gives.
 *
 * `white-space: nowrap` on the header cell is what actually guarantees it — the
 * browser sizes the column to the text. The explicit min-width is a FLOOR for
 * short headers ("Grants" over a column of five-digit numbers), not a fit, and
 * it is expressed in `ch` so it tracks the font rather than assuming a pixel
 * width that changes the moment the type scale does.
 *
 * ---------------------------------------------------------------------------
 * ROW CLICKS PASS THE ROW, NOT ITS POSITION
 *
 * `onRowClick` hands back the row object. The previous caller read `rowIndex`
 * off the clicked `<tr>` and subtracted one to skip the header — correct only
 * while the table has exactly one header row and no footer above the body.
 * Adding a totals row would have silently shifted every click by one, and on
 * the permissions screen that means opening the wrong person's grants.
 */
export function DataGrid<T>({
  columns,
  rows,
  totalsLabel,
  totalsTitle,
  totals,
  emptyMessage = 'No rows.',
  onRowClick,
  isRowActive,
  fill = false,
  surface,
  gridId,
  sort,
  onSortChange,
  totalRows,
  virtual = false,
}: {
  columns: Column<T>[]
  rows: T[]
  totalsLabel?: string
  /** What the totals label means, on hover -- e.g. why the total is "mixed units". */
  totalsTitle?: string
  /**
   * TOTALS THE SERVER WORKED OUT, by column key, in place of adding up the rows
   * on screen. For a PAGED grid the rows here are one page, so their sum is the
   * page's -- which is how Material Planning's order screens showed a filtered
   * view a bigger "total" than the unfiltered one (7 Oct 2026). Given, every
   * `total` column shows its entry here, or nothing; the page is never summed.
   */
  totals?: Partial<Record<string, ReactNode>>
  emptyMessage?: string
  /**
   * SERVER-SORTED MODE. Pass both and this grid stops reordering anything: it
   * renders the arrow, reports the click, and leaves the order to whoever
   * fetched the rows.
   *
   * Required for any grid that shows a PAGE of a larger set — see `totalRows`.
   */
  sort?: SortState
  onSortChange?: (next: SortState) => void
  /**
   * How many rows exist in total, when these are only some of them.
   *
   * THIS IS A SAFETY INTERLOCK, not a display value. Sorting a hundred rows
   * out of nineteen thousand in the browser produces something that looks
   * exactly like a sorted report and is not one. So when this says there are
   * more rows than were handed over and the caller has NOT supplied
   * `onSortChange`, the headings simply do not sort — the grid refuses rather
   * than quietly answering the wrong question. Omit it for a grid that holds
   * its whole population and client sorting applies as normal.
   */
  totalRows?: number
  /** Makes rows interactive. Omit and rows stay inert. */
  onRowClick?: (row: T) => void
  /** Marks the currently-open row, so the detail panel below has a visible source. */
  isRowActive?: (row: T) => boolean
  /**
   * Fill the remaining height of a flex column and own the vertical scrollbar.
   *
   * WHY THIS IS A PROP AND NOT A WRAPPER AT THE CALL SITE
   * The obvious alternative — putting `<DataGrid>` inside somebody else's
   * `overflow-auto` div — produces TWO scroll containers, one inside the other:
   * the outer scrolls down, this one scrolls across, and a mouse wheel over the
   * table does whichever the browser guesses. Worse, `position: sticky` resolves
   * against the nearest scrolling ancestor, so the header would stick to the
   * wrong box and slide away under a tall list — and this grid's whole
   * contract, stated above, is that the header is always readable.
   *
   * So the element that scrolls has to be this one, and only the caller knows
   * whether it should fill.
   */
  fill?: boolean
  /**
   * DRAW ONLY THE ROWS IN VIEW, for a long list in a grid that fills its box
   * (owner, 29 Oct 2026: the Users page "lagging, and names disappearing while
   * scrolling", 1,188 people). Every row drawn costs layout, and a pinned
   * column's cell in every row is a layer of its own that the browser must
   * repaint as the list moves; past a few hundred it falls behind and those
   * cells show blank. With this on, and more than VIRTUAL_FROM rows, the rows
   * in view and OVERSCAN either side are drawn, and one spacer row above and
   * one below stand for the rest, so the scrollbar is still the whole list's.
   *
   * Two things follow, both deliberate. A column only grows: each keeps the
   * widest it has been while scrolling, so a column never narrows and shifts
   * back. And the browser's own find (Ctrl+F) reaches only the rows drawn; a
   * page that turns this on has its own search. Opt-in, so a report grid that
   * prints or measures every row keeps doing so.
   */
  virtual?: boolean
  /**
   * Which documented surface these columns belong to, for developer mode.
   *
   * Omit it and every header stays inert, which is right for the admin grids:
   * they show grants and roles rather than figures, and there is nothing about
   * them a business reviewer needs explained. Name it and each column whose
   * `key` has a definition behind it becomes clickable — no other change at the
   * call site, and no risk of a stale list, because the server says which keys
   * are documented.
   */
  surface?: string
  /**
   * Names this grid, which turns on the reader's own column layout: resize,
   * reorder, show/hide and pin, remembered under this id for the session.
   *
   * Opt-in by name rather than on by default, because the id IS the memory. Two
   * grids sharing one would trade layouts, and a grid that changes its columns
   * between tabs (Material Planning's six) would look like it had lost them.
   * Omit it — as the admin tables do — and the grid behaves exactly as before.
   */
  gridId?: string
}) {
  const hasTotals = totalsLabel !== undefined && columns.some((c) => c.total)
  const interactive = Boolean(onRowClick)
  const layout = useGridLayout(gridId, columns)
  const enabled = Boolean(gridId)
  const shown = layout.columns

  /* SORTING, FOR EVERY GRID THAT RENDERS THROUGH HERE — six Material Planning
     screens and five administration tables, none of which sorted before
     20 Sep 2026 although `Column.value` has been documented as "pulls the
     sortable/summable number out of a row" the whole time.

     The interlock, stated once: client-side sorting is offered only when this
     grid genuinely holds every row. `totalRows` greater than what arrived
     means somebody is paging, and a browser sort would reorder the visible
     page while looking exactly like a report-wide sort. Such a caller must
     pass `onSortChange` and sort on the server; if it does not, the headings
     go inert instead of lying. */
  const serverSorted = Boolean(onSortChange)
  const partial = totalRows !== undefined && totalRows > rows.length
  const canSort = serverSorted || !partial
  const local = useGridSort()
  const active = serverSorted ? (sort ?? { key: null, desc: true }) : local.sort

  const toggle = useCallback(
    (key: string) => {
      if (!canSort) return
      if (!serverSorted) return local.toggle(key)
      // The same three-step cycle the local hook implements, kept identical
      // here so a server-sorted grid never behaves differently under the
      // reader's hand: new column descending, flip, then back to natural.
      const next: SortState =
        active.key !== key
          ? { key, desc: true }
          : active.desc
            ? { key, desc: false }
            : { key: null, desc: true }
      onSortChange?.(next)
    },
    [canSort, serverSorted, local, active, onSortChange],
  )

  /* A column sorts by its `value` accessor where it has one, and by the raw
     field otherwise — the same two sources the cell itself renders from, so
     the order always matches what is on screen rather than some other reading
     of the row. `render` is deliberately NOT consulted: it returns nodes, and
     a badge or a component cannot be compared. */
  /* A HEADING OFFERS A CLICK ONLY WHERE THE CLICK LEADS SOMEWHERE — the same
     principle `Explain` applies to documentation. A column with no accessor
     and no matching field on the row has nothing to compare, and rendering it
     as clickable buys a reader a dead control they will read as a broken
     grid. Server-sorted grids are the caller's business and always clickable. */
  const canSortColumn = useCallback(
    (c: Column<T>) => {
      if (!canSort) return false
      // Server-sorted: only a column that knows its server-side name, because
      // a name the procedure does not recognise is dropped in silence.
      if (serverSorted) return Boolean(c.sortKey)
      if (c.sortValue || c.value) return true
      return rows.length > 0 && (rows[0] as never)[c.key] !== undefined
    },
    [canSort, serverSorted, rows],
  )

  /** A server-sorted grid is addressed by `sortKey`; a local one by `key`. */
  const sortNameOf = useCallback(
    (c: Column<T>) => (serverSorted ? (c.sortKey ?? c.key) : c.key),
    [serverSorted],
  )

  const sorted = useMemo(() => {
    if (serverSorted || !active.key) return rows
    const col = columns.find((c) => c.key === active.key)
    if (!col) return rows
    const pick = (row: T): number | string | null =>
      col.sortValue
        ? col.sortValue(row)
        : col.value
          ? col.value(row)
          : ((row as never)[col.key] ?? null)
    return [...rows].sort((a, b) => compareBy(pick(a), pick(b), active.desc))
  }, [rows, columns, active, serverSorted])
  const [dragKey, setDragKey] = useState<string | null>(null)
  const [overKey, setOverKey] = useState<string | null>(null)

  /* IS THERE MORE TO THE RIGHT, AND HAS THE READER SEEN IT?
     Nothing in any grid said so. Purchase Orders hides 2,914px past the right
     edge, Transactions 1,964px, Stock 1,824px — and a reader on a 15.6in
     laptop had no way to know that 26 of 34 columns existed. The shadow is
     painted only while there is genuinely something hidden, and it retreats as
     the reader reaches the end, so it reads as an edge rather than as
     decoration. */
  const scroller = useRef<HTMLDivElement | null>(null)
  const [edge, setEdge] = useState({ left: false, right: false })
  const edgeNow = useRef(edge)
  // Where the list is scrolled to, for `virtual`: read once a frame, not once an event.
  const [view, setView] = useState({ top: 0, height: 0, width: 0 })
  const viewNow = useRef(view)
  const frame = useRef(0)
  const onScroll = useCallback(() => {
    const el = scroller.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    const left = el.scrollLeft > 2
    const right = max > 2 && el.scrollLeft < max - 2
    // THE SAME STATE IS NOT A NEW STATE (29 Oct 2026). A fresh object on every
    // scroll event re-rendered every row of the grid on every event: 1,188
    // people, each tick of the wheel. Compared here, before React is asked:
    // even an updater that hands back the same state costs React a render to
    // find that out.
    if (edgeNow.current.left !== left || edgeNow.current.right !== right) {
      edgeNow.current = { left, right }
      setEdge(edgeNow.current)
    }
    if (virtual && !frame.current) {
      frame.current = requestAnimationFrame(() => {
        frame.current = 0
        if (viewNow.current.top === el.scrollTop && viewNow.current.height === el.clientHeight && viewNow.current.width === el.clientWidth) return
        viewNow.current = { top: el.scrollTop, height: el.clientHeight, width: el.clientWidth }
        setView(viewNow.current)
      })
    }
  }, [virtual])
  // Cancelled and cleared: a frame cancelled but still marked pending would block every later one
  // (React mounts twice in development, and the list never moved).
  useEffect(() => () => { cancelAnimationFrame(frame.current); frame.current = 0 }, [])
  useEffect(() => {
    onScroll()
    const el = scroller.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(onScroll)
    ro.observe(el)
    return () => ro.disconnect()
  }, [onScroll, columns, rows])

  /* THE ROWS DRAWN, under `virtual` (see the prop). Row height is the AVERAGE of
     the rows drawn, measured when the list or the columns change and not as the
     window moves: rows are 52 or 53px, and a height read as it moved moved the
     window again, round and round, and cost a second draw of every frame. The
     header's is measured with it, so the spacers stand for the rows they replace. */
  const windowed = virtual && fill && sorted.length > VIRTUAL_FROM
  const head = useRef<HTMLTableSectionElement | null>(null)
  const body = useRef<HTMLTableSectionElement | null>(null)
  const [rowHeight, setRowHeight] = useState(48)
  const [headHeight, setHeadHeight] = useState(40)
  // Clamped to the list as it is now: a filter that shortens it while scrolled far down left the
  // window past its end and drew nothing until the scroll caught up (review of 19m).
  const viewHeight = view.height || 800
  const top = Math.min(view.top, Math.max(0, headHeight + sorted.length * rowHeight - viewHeight))
  const to = windowed ? Math.min(sorted.length, Math.ceil((top - headHeight + viewHeight) / rowHeight) + OVERSCAN) : sorted.length
  const from = windowed ? Math.min(to, Math.max(0, Math.floor((top - headHeight) / rowHeight) - OVERSCAN)) : 0
  /* A COLUMN ONLY GROWS while the rows drawn change: each header keeps the widest
     it has been, so a longer name further down widens its column once, and it
     never narrows back. Forgotten when the columns, the list, a width the reader
     set (a drag, an auto-fit, a reset) or the box's width change, so a column can
     still be narrowed and a narrower window is not held at the old widths. Only a
     windowed grid keeps it; any other is told nothing (review of 19m). */
  const [grown, setGrown] = useState<Record<string, number>>({})
  const widthsKey = shown.map((c) => layout.widthOf(c.key) ?? '').join('|')
  useEffect(() => {
    if (windowed) setGrown((g) => (Object.keys(g).length ? {} : g))
  }, [windowed, columns, rows, widthsKey, view.width])
  useLayoutEffect(() => {
    if (!windowed) return
    const drawn = body.current ? Array.from(body.current.rows).filter((r) => !r.hasAttribute('aria-hidden')) : []
    const average = drawn.length ? drawn.reduce((sum, r) => sum + r.offsetHeight, 0) / drawn.length : 0
    if (average > 0) setRowHeight((h) => (Math.abs(average - h) > 1 ? average : h))
    const h = head.current?.offsetHeight ?? 0
    if (h > 0) setHeadHeight((x) => (Math.abs(h - x) > 0.5 ? h : x))
  }, [windowed, columns, rows])
  useLayoutEffect(() => {
    if (!windowed) return
    let next: Record<string, number> | null = null
    for (const c of shown) {
      const w = layout.headerWidth(c.key)
      if (w > (grown[c.key] ?? 0) + 0.5) next = { ...(next ?? grown), [c.key]: w }
    }
    if (next) setGrown(next)
  })

  /* EACH ROW IS DRAWN ONCE, not again on every scroll (29 Oct 2026): a row is its own
     remembered component, and the columns' facts it reads are one array kept while
     nothing about the columns changes, so moving the window draws only the rows
     coming into it. */
  const cellsKey = shown.map((c) => `${c.key}:${layout.pinOf(c.key) ?? ''}:${layout.widthOf(c.key) ?? ''}:${layout.offsetOf(c.key)}`).join('|')
  const cells = useMemo(
    () => shown.map((c) => ({ c, pin: layout.pinOf(c.key), w: layout.widthOf(c.key), offset: layout.offsetOf(c.key) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the key holds every fact read here
    [cellsKey, columns],
  )

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {enabled && <GridChrome api={layout} />}
      {edge.left && (
        <div aria-hidden className="pointer-events-none absolute inset-y-0 left-0 z-[3] w-4 bg-gradient-to-r from-black/10 to-transparent" />
      )}
      {edge.right && (
        <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 z-[3] w-4 bg-gradient-to-l from-black/10 to-transparent" />
      )}
    <div
      ref={scroller}
      onScroll={onScroll}
      className={fill ? 'scroll-slim min-h-0 flex-1 overflow-auto' : 'scroll-slim overflow-x-auto'}
    >
      {/* border-SEPARATE, deliberately — the same fix as ReceivablesMatrix. A
          pinned (sticky) body cell is its own compositor layer with an opaque
          background; over a border-collapse table's shared row edges it paints
          the line out on alternate rows at fractional row heights. Each cell
          owns its bottom border here, which nothing can cover. */}
      <table className="report-table w-full min-w-max border-separate border-spacing-0 text-(length:--text-body)"
        // The whole list's count when only some rows are drawn: a header row and every body row.
        aria-rowcount={windowed ? sorted.length + 1 : undefined}>
        <thead ref={head}>
          <tr>
            {shown.map((c) => {
              const pin = layout.pinOf(c.key)
              const w = layout.widthOf(c.key)
              return (
              <th
                key={c.key}
                ref={layout.headerRef(c.key)}
                scope="col"
                // Always one of the three values on a grid that can sort, and
                // absent on one that cannot: "none" says "sortable, not
                // sorted", which is a different statement from silence.
                aria-sort={canSortColumn(c) ? (active.key === sortNameOf(c) ? (active.desc ? 'descending' : 'ascending') : 'none') : undefined}
                /* REORDERING IS A HEADER DRAG, the gesture every grid the
                   manager named uses. The drop target is whichever header the
                   pointer is over; the panel behind the Columns button does the
                   same move from the keyboard, for the reader who cannot drag
                   and for the 34-column grids where the target is off-screen. */
                draggable={enabled}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move'
                  e.dataTransfer.setData('text/plain', c.key)
                  setDragKey(c.key)
                }}
                onDragOver={(e) => {
                  if (!dragKey || dragKey === c.key) return
                  e.preventDefault()
                  setOverKey(c.key)
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  if (dragKey && dragKey !== c.key) layout.move(dragKey, layout.indexOf(c.key))
                  setDragKey(null)
                  setOverKey(null)
                }}
                onDragEnd={() => {
                  setDragKey(null)
                  setOverKey(null)
                }}
                // z-20 on the pinned column: it is the corner cell, sticky on
                // both axes, and has to sit above the rest of the header (z-10)
                // as well as above the pinned body cells.
                className={`relative sticky top-0 border-b border-(--color-line-strong) px-(--cell-x) py-(--head-y) font-semibold whitespace-nowrap text-(--color-ink) ${
                  'text-center'
                } ${
                  pin === 'right'
                    ? 'z-20 border-l border-(--color-line)'
                    : pin === 'left'
                      ? 'z-20 border-r border-(--color-line)'
                      : 'z-10'
                } ${overKey === c.key ? 'bg-(--color-navy-100)' : ''} ${dragKey === c.key ? 'opacity-50' : ''}`}
                /* THE FLOOR IS THE HEADER, and the headroom is the padding.
                   `ch` is the width of "0", and headers are mixed-case words
                   whose mean glyph is narrower, so a ch-per-character floor
                   already over-pays. With 2.5rem (40px) added on top of real
                   padding of 28px, the 34-column Purchase Orders grid asked
                   for 3,771px where its headers plus padding need 3,069px —
                   702px, 19%, of pure air that the reader has to scroll past.
                   1.75rem matches the px-3.5 the cells actually use.

                   A width the reader has dragged replaces the floor entirely —
                   it can never be under the header's own width, because the
                   drag clamps there, so the header still cannot truncate.

                   NO CAP ON A FIGURE COLUMN (4 Oct 2026): the dragged width is
                   a floor there, so a wider number widens the column rather
                   than being cut. Text keeps its cap; see rule 1 in
                   gridLayout.tsx. */
                style={{
                  minWidth: w ? w : grown[c.key] ? `max(calc(${c.header.length}ch + var(--cell-x) * 2), ${grown[c.key]}px)` : `calc(${c.header.length}ch + var(--cell-x) * 2)`,
                  width: w,
                  maxWidth: c.align === 'right' ? undefined : w,
                  left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
                  right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
                }}
              >
                {/* SORTING AND DEVELOPER MODE, FOR EVERY GRID ON THE PLATFORM,
                    FROM HERE. `SortHeader` renders a plain label unless this
                    grid may sort, and adds the explanation mark only when the
                    reader has developer mode on AND this column has a
                    definition behind it. So this line is inert for everybody
                    else, and it needed no change at a single call site.

                    That is the whole reason it belongs here rather than in the
                    pages. Material Planning has six grids across ten screens
                    and the ledger has twenty-five columns; wiring each one by
                    hand is 134 chances to pass the wrong surface, and the
                    symptom of passing the wrong surface is a panel that
                    confidently explains a different column.

                    A grid whose caller names no `surface` is simply never
                    clickable — the admin tables have no documentation and want
                    none. */}
                <SortHeader
                  label={c.header}
                  active={active.key === sortNameOf(c)}
                  desc={active.desc}
                  onToggle={canSortColumn(c) ? () => toggle(sortNameOf(c)) : undefined}
                  surface={surface}
                  measureKey={surface ? c.key : undefined}
                />
                {enabled && (
                  <ResizeHandle
                    label={c.header}
                    widthOf={() => layout.headerWidth(c.key)}
                    minWidth={() => layout.minWidthOf(c.key)}
                    onResize={(px) => layout.resize(c.key, px)}
                    onAutoFit={() => layout.autoFit(c.key)}
                  />
                )}
              </th>
            )})}
          </tr>
        </thead>

        <tbody ref={body}>
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={shown.length}
                className="px-3.5 py-10 text-center text-(--color-ink-3)"
              >
                {emptyMessage}
              </td>
            </tr>
          )}

          {from > 0 && (
            <tr aria-hidden style={{ height: from * rowHeight }}>
              <td colSpan={shown.length} className="p-0" />
            </tr>
          )}
          {sorted.slice(from, to).map((row, j) => (
            <GridRow key={from + j} row={row} i={from + j} cells={cells} rowActive={isRowActive?.(row) ?? false}
              interactive={interactive} onRowClick={onRowClick} rowIndex={windowed ? from + j + 2 : undefined} />
          ))}
          {to < sorted.length && (
            <tr aria-hidden style={{ height: (sorted.length - to) * rowHeight }}>
              <td colSpan={shown.length} className="p-0" />
            </tr>
          )}
        </tbody>

        {hasTotals && rows.length > 0 && (
          <tfoot>
            {/* The rule above the totals is on the CELLS: a row border does not
                render in border-separate mode. */}
            {/* STICKY, because the total is what the report was opened for.
                A grid box is about 400px tall on a 720px laptop — roughly
                eleven rows — so on any report with more rows than that the
                Total scrolled out of sight and the reader had to travel to the
                bottom of five hundred rows to see the one number they came
                for. z-[2] clears the pinned body cells (z-[1]); the background
                is opaque so rows pass underneath rather than through. */}
            <tr className="bg-(--color-surface-3) font-semibold">
              {shown.map((c, i) => (
                <td
                  key={c.key}
                  title={i === 0 ? totalsTitle : undefined}
                  className={`sticky bottom-0 z-[2] border-t-2 border-(--color-line-strong) bg-(--color-surface-3) px-(--cell-x) py-(--head-y) text-(--color-ink) ${
                    c.align === 'right' ? 'num' : ''
                  } ${layout.pinOf(c.key) === 'left' ? 'z-[3] border-r border-(--color-line)' : ''} ${
                    layout.pinOf(c.key) === 'right' ? 'z-[3] border-l border-(--color-line)' : ''
                  }`}
                  style={{
                    width: layout.widthOf(c.key),
                    // A total is a figure: its column's width is a floor too.
                    maxWidth: c.align === 'right' ? undefined : layout.widthOf(c.key),
                    left: layout.pinOf(c.key) === 'left' ? layout.offsetOf(c.key) : undefined,
                    right: layout.pinOf(c.key) === 'right' ? layout.offsetOf(c.key) : undefined,
                  }}
                >
                  {i === 0
                    ? totalsLabel
                    : totals
                      ? (c.total ? (totals[c.key] ?? '') : '')
                      : c.total && c.value
                        ? totalOf(c, rows)
                        : ''}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
    </div>
  )
}

/** A column's total: the plain sum, or what the new model says about it (Column.withheld). */
/** One body row of the grid: drawn again only when its row, its place or the columns change. */
function GridRowView<T>({ row, i, cells, rowActive, interactive, onRowClick, rowIndex }: {
  row: T
  i: number
  /** Its place in the whole list, for a screen reader, when only some rows are drawn (aria-rowindex). */
  rowIndex?: number
  cells: { c: Column<T>; pin: Pin; w: number | undefined; offset: number }[]
  rowActive: boolean
  interactive: boolean
  onRowClick?: (row: T) => void
}) {
  const pinnedTint = rowActive
    ? 'bg-(--color-navy-100)'
    : `bg-(--color-surface) ${interactive ? 'group-hover:bg-(--color-navy-50) group-focus-visible:bg-(--color-navy-50)' : ''}`
  return (
    <tr
      aria-rowindex={rowIndex}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
      // Keyboard reaches this too. A grid whose only way in is a mouse
      // is a grid half the auditors on this platform cannot use.
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        onRowClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onRowClick(row)
              }
            }
          : undefined
      }
      // `group` so a pinned cell can mirror the row's hover tint.
      className={`group transition-colors ${
        rowActive ? 'bg-(--color-navy-100)' : ''
      } ${
        interactive
          ? 'cursor-pointer outline-none hover:bg-(--color-navy-50) focus-visible:bg-(--color-navy-50) focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-(--color-accent)'
          : ''
      }`}
    >
      {cells.map(({ c, pin, w, offset }) => {
        const content = c.render
          ? c.render(row)
          : c.value
            ? money(c.value(row))
            : String((row as never)[c.key] ?? '')
        /* A FIGURE IS NEVER CLIPPED (4 Oct 2026). Only free text may
           be: a reader-narrowed or clamped text column truncates with
           the full value in the title, while a figure column's width
           is a floor and the number widens it. */
        const figure = c.align === 'right'
        const clips = !figure && (!!c.clamp || !!w)

        return (
          <td
            key={c.key}
            // The full text on hover whenever the cell can clip. Only
            // for plain strings: a rendered cell may be a badge or a
            // whole component, and stringifying one produces
            // "[object Object]" in the tooltip.
            title={clips && typeof content === 'string' ? content : undefined}
            className={`${i === 0 ? '' : 'border-t'} border-(--color-line) leading-5 px-(--cell-x) py-(--cell-y) text-(--color-ink-2) ${figure ? 'num' : ''} ${
              /* A column the reader has narrowed clips its CONTENT —
                 their explicit choice, and the full value stays one
                 hover away in the title, exactly as `clamp` does.
                 Text only: see `figure` above. */
              clips ? 'truncate' : ''
            } ${
              // A PINNED CELL NEEDS ITS OWN BACKGROUND, or the rows it
              // floats over show straight through it. It also has to
              // repeat the row's hover and active tints by hand: those
              // are painted on the <tr>, and a positioned cell is
              // composited above its own row's background. The ROW's
              // active state, never `active` (the sort, which is always
              // set): that painted every pinned cell selected-blue and
              // hid the hover there (owner, 27 Oct 2026). The hover and
              // focus tints only where the row has them (`interactive`),
              // or a still grid would tint the pinned cell alone. And it
              // fades as the row does (`transition-colors`): switching at
              // once while the row faded, a quick move lit one row's pinned
              // cell beside another row's other cells (owner, 29 Oct 2026).
              pin === 'right'
                ? `sticky z-[1] border-l border-(--color-line) transition-colors ${pinnedTint}`
                : pin === 'left'
                  ? /* The pinned cell is its own compositor layer, so
                       it needs an opaque background of its own or the
                       rows scrolling underneath show straight through
                       it — and it has to repeat the row's hover and
                       active tints by hand, because those are painted
                       on the <tr> and a positioned cell composites
                       above its own row's background. */
                    `sticky z-[1] border-r border-(--color-line) transition-colors ${pinnedTint}`
                  : ''
            }`}
            style={{
              maxWidth: figure ? undefined : (w ?? (c.clamp ? `${c.clamp}ch` : undefined)),
              width: w,
              left: pin === 'left' ? offset : undefined,
              right: pin === 'right' ? offset : undefined,
            }}
          >
            {content}
          </td>
        )
      })}
    </tr>
  )
}
const GridRow = memo(GridRowView) as typeof GridRowView

function totalOf<T>(c: Column<T>, rows: T[]): ReactNode {
  const sum = money(rows.reduce((s, r) => s + c.value!(r), 0))
  if (!c.withheld || rows.length === 0) return sum
  const states = rows.map(c.withheld)
  if (states.every((s) => s === 'withheld')) return <Withheld />
  return states.some((s) => s !== null) ? <PartlyWithheld>{sum}</PartlyWithheld> : sum
}
